// src/pages/Settings.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Bell, Moon, Sun, Globe, Check, X, Loader2, Trash2, CreditCard, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react'; // Importar iconos necesarios

// Hook personalizado para obtener token (asume que lo tienes o lo defines)
const useAuthToken = () => {
    return localStorage.getItem('access_token');
};

// Hook personalizado para llamadas API (asume que lo tienes o lo defines)
const useApi = () => {
    const navigate = useNavigate();
    const getToken = useAuthToken;

    const makeApiCall = useCallback(async (endpoint, method = 'GET', body = null, isFormData = false) => {
        const token = getToken();
        // Solo redirige si no hay token Y la ruta NO es pública (ajusta según tus rutas públicas)
        const publicRoutes = ['/login', '/register', '/verify-email'];
        if (!token && !publicRoutes.includes(endpoint)) {
            console.error("No token found, redirecting to landing.");
            navigate('/'); // Redirige a landing para modal
            throw new Error("Authentication token not found.");
        }

        const headers = {
            'Authorization': token ? `Bearer ${token}` : '',
            'Accept': 'application/json',
        };
        if (body && !isFormData) {
            headers['Content-Type'] = 'application/json';
        }

        const config = {
            method,
            headers,
            body: isFormData ? body : (body ? JSON.stringify(body) : null),
        };

        // Asumimos que el backend está en el mismo origen o configurado con proxy
        // Si no, necesitas la URL completa: process.env.REACT_APP_API_URL || 'http://localhost:5000'
        const apiUrlBase = process.env.REACT_APP_API_URL || ''; // Ajusta si es necesario
        const url = `${apiUrlBase}/api${endpoint}`;

        try {
            console.log(`API Call: ${method} ${url}`); // Log para depuración
            const response = await fetch(url, config);

            // Manejo específico para 401/422 (token inválido/expirado)
            if (response.status === 401 || response.status === 422) {
                console.error(`API Error ${response.status}: Invalid or expired token.`);
                localStorage.removeItem('access_token');
                navigate('/'); // Redirige a landing para modal
                throw new Error("Unauthorized or invalid token.");
            }

            // Manejar respuestas sin contenido (ej. DELETE exitoso)
            if (response.status === 204 || response.headers.get("content-length") === "0") {
                console.log(`API Response ${response.status}: No Content`);
                return { ok: response.ok, status: response.status, data: null };
            }

            // Intentar parsear JSON
            const data = await response.json();
            console.log(`API Response ${response.status}:`, data);

            return { ok: response.ok, status: response.status, data };

        } catch (error) {
            // Evitar doble redirección si el error ya fue de token
            if (error.message !== "Authentication token not found." && error.message !== "Unauthorized or invalid token.") {
                 console.error(`API call error (${method} ${url}):`, error);
                 throw error; // Relanzar otros errores (red, etc.)
            }
             // Si ya se manejó el error de token, simplemente no hacer nada más aquí
        }
    }, [getToken, navigate]);

    return makeApiCall;
};


// --- Componente Modal de Confirmación Simple ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, isLoading }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
            <div className="relative bg-gray-800 border border-red-500/50 rounded-xl shadow-2xl max-w-md w-full p-6 m-4">
                <div className="text-center">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
                    <p className="text-sm text-gray-300 mb-6">{message}</p>
                </div>
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-5 py-2 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-500 rounded-md transition-colors disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className="px-5 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Confirmar Eliminación'}
                    </button>
                </div>
            </div>
        </div>
    );
};


function Settings() {
    const navigate = useNavigate();
    const makeApiCall = useApi();

    // --- Estados Generales ---
    const [userData, setUserData] = useState({ username: '', email: '', isPremium: false });
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [errorLoadingUser, setErrorLoadingUser] = useState(null);

    // --- Estados para Cambio de Contraseña ---
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    // const [currentPassword, setCurrentPassword] = useState(''); // Descomentar si backend requiere contraseña actual
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [passwordUpdateStatus, setPasswordUpdateStatus] = useState({ message: '', type: '' });
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    // --- Estados para Eliminación de Cuenta ---
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    // --- Estados para Preferencias (localStorage) ---
    const [language, setLanguage] = useState(() => localStorage.getItem('appLanguage') || 'es');
    const [theme, setTheme] = useState(() => localStorage.getItem('appTheme') || 'dark');
    const [notificationsEnabled, setNotificationsEnabled] = useState(() => localStorage.getItem('appNotifications') === 'true');

    // --- Efecto para cargar datos del usuario ---
    useEffect(() => {
        const fetchUserData = async () => {
            setIsLoadingUser(true);
            setErrorLoadingUser(null);
            try {
                // Asegurarse de que makeApiCall esté disponible
                if (!makeApiCall) {
                    console.error("makeApiCall no está definido todavía.");
                    setErrorLoadingUser("Error interno al inicializar la API.");
                    return;
                }
                const result = await makeApiCall('/me'); // Llama a la función retornada por useApi
                if (result && result.ok) {
                    setUserData(result.data);
                } else if (result) { // Si hubo respuesta pero no fue ok (y no fue 401/422 manejado por el hook)
                    setErrorLoadingUser(result.data?.error || "No se pudieron cargar los datos del usuario.");
                }
                 // El caso de error de red o token inválido es manejado por el hook useApi
            } catch (error) {
                // Solo mostrar error si no fue manejado por el hook (ej. error de red)
                 if (error.message !== "Authentication token not found." && error.message !== "Unauthorized or invalid token.") {
                    setErrorLoadingUser("Error de red al cargar los datos.");
                 }
            } finally {
                setIsLoadingUser(false);
            }
        };
        fetchUserData();
    }, [makeApiCall]); // makeApiCall es una dependencia estable si usas useCallback

    // --- Efectos para guardar preferencias en localStorage ---
    useEffect(() => { localStorage.setItem('appLanguage', language); }, [language]);
    useEffect(() => {
        localStorage.setItem('appTheme', theme);
        document.documentElement.classList.toggle('dark', theme === 'dark');
    }, [theme]);
    useEffect(() => { localStorage.setItem('appNotifications', notificationsEnabled); }, [notificationsEnabled]);

    // --- Manejadores ---
    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        setPasswordUpdateStatus({ message: '', type: '' }); // Limpiar mensaje previo

        if (newPassword !== repeatPassword) {
            setPasswordUpdateStatus({ message: 'Las nuevas contraseñas no coinciden.', type: 'error' });
            return;
        }
        if (newPassword.length < 6) {
            setPasswordUpdateStatus({ message: 'La contraseña debe tener al menos 6 caracteres.', type: 'error' });
            return;
        }

        setIsUpdatingPassword(true);

        try {
            // Asume endpoint PUT /api/user/password que solo necesita la nueva contraseña
            const body = { new_password: newPassword };
            // Descomenta si necesitas enviar la actual:
            // const body = { current_password: currentPassword, new_password: newPassword };

            const result = await makeApiCall('/user/password', 'PUT', body);

            if (result && result.ok) {
                setPasswordUpdateStatus({ message: 'Contraseña actualizada con éxito.', type: 'success' });
                setIsEditingPassword(false);
                // Limpiar campos después de éxito
                // setCurrentPassword('');
                setNewPassword('');
                setRepeatPassword('');
                // Ocultar mensaje de éxito después de un tiempo
                setTimeout(() => setPasswordUpdateStatus({ message: '', type: '' }), 3000);
            } else {
                setPasswordUpdateStatus({ message: result?.data?.error || 'Error al actualizar la contraseña.', type: 'error' });
            }
        } catch (error) {
             // Errores de red o token inválido ya son manejados por useApi (redirección)
             // Solo mostrar error si es otro tipo
             if (error.message !== "Authentication token not found." && error.message !== "Unauthorized or invalid token.") {
                setPasswordUpdateStatus({ message: 'Error de conexión al actualizar contraseña.', type: 'error' });
             }
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    // --- Manejadores para el Modal de Eliminación ---
    const openDeleteModal = () => {
        setDeleteError(''); // Limpiar error previo
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        if (!isDeletingAccount) { // No cerrar si está en proceso
             setIsDeleteModalOpen(false);
        }
    };

    const handleDeleteAccountConfirm = async () => {
        setIsDeletingAccount(true);
        setDeleteError('');

        try {
            // Llama al endpoint DELETE /api/user
            const result = await makeApiCall('/user', 'DELETE');

            if (result && (result.ok || result.status === 204)) { // Éxito o Sin Contenido
                console.log("Cuenta eliminada exitosamente.");
                localStorage.removeItem('access_token'); // Limpiar token
                 // Podrías limpiar otras cosas del localStorage si es necesario
                 // localStorage.clear();
                navigate('/'); // Redirigir a landing
            } else {
                setDeleteError(result?.data?.error || 'Error al eliminar la cuenta.');
                setIsDeletingAccount(false); // Permitir reintentar si falla
                // No cerrar el modal si hay error
            }
        } catch (error) {
             // Errores de red o token inválido ya son manejados por useApi (redirección)
             if (error.message !== "Authentication token not found." && error.message !== "Unauthorized or invalid token.") {
                  setDeleteError('Error de conexión al eliminar la cuenta.');
                  setIsDeletingAccount(false); // Permitir reintentar
             }
             // Si fue error de token, ya se habrá redirigido
        }
        // No ponemos finally aquí, porque si tiene éxito, navega fuera.
        // El estado de carga se resetea solo si hay error.
    };


    // --- Renderizado ---
    if (isLoadingUser) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <Loader2 className="h-12 w-12 text-purple-400 animate-spin" />
            </div>
        );
    }

    if (errorLoadingUser) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-center p-4">
                <XCircle className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Error al Cargar</h2>
                <p className="text-gray-300 mb-6">{errorLoadingUser}</p>
                <button
                    onClick={() => navigate('/DesignStudio')} // Ir a DesignStudio en lugar de /design
                    className="inline-block px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-medium text-sm transition-colors duration-300"
                >
                    Ir al Estudio
                </button>
            </div>
        );
    }

    // --- Renderizado Principal ---
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 to-gray-900 text-gray-200 pb-16">
            {/* --- Header --- */}
            <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-40 mb-8">
                 <nav className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                   <div className="flex justify-between items-center h-16">
                     <div className="flex items-center space-x-4">
                        <button onClick={() => navigate('/DesignStudio')} className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/50" title="Volver al Estudio">
                            <ArrowLeft size={20} />
                        </button>
                       <h1 className="text-lg font-semibold text-white">Configuración</h1>
                     </div>
                     {/* Podría ir un botón global de "Guardar" si las preferencias se guardaran en bloque */}
                   </div>
                 </nav>
            </header>

            {/* --- Contenido Principal --- */}
            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

                {/* --- Sección: Perfil --- */}
                <section aria-labelledby="profile-heading">
                    <h2 id="profile-heading" className="text-xl font-semibold text-purple-300 mb-4 border-b border-gray-700 pb-2">Perfil</h2>
                    <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl shadow-lg p-6 space-y-4">
                        <div className="flex items-center space-x-4">
                           <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-xl font-bold text-white flex-shrink-0">
                               {userData.username?.charAt(0)?.toUpperCase() || '?'}
                           </span>
                           <div className="overflow-hidden"> {/* Para truncar si es necesario */}
                               <p className="text-lg font-medium text-white truncate">{userData.username}</p>
                               <p className="text-sm text-gray-400 truncate">{userData.email}</p>
                           </div>
                        </div>
                         {/* Añadir botón para editar perfil si se implementa */}
                    </div>
                </section>

                {/* --- Sección: Seguridad (Cambio de Contraseña) --- */}
                <section aria-labelledby="security-heading">
                     <h2 id="security-heading" className="text-xl font-semibold text-purple-300 mb-4 border-b border-gray-700 pb-2">Seguridad</h2>
                     <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl shadow-lg p-6">
                         {!isEditingPassword ? (
                           <div className="flex justify-between items-center">
                               <div>
                                   <h3 className="text-base font-medium text-white">Contraseña</h3>
                                   <p className="text-sm text-gray-400">••••••••</p>
                               </div>
                               <button
                                  onClick={() => { setIsEditingPassword(true); setPasswordUpdateStatus({ message: '', type: '' }); }}
                                  className="px-4 py-1.5 text-sm font-medium text-purple-300 bg-gray-700/50 hover:bg-gray-700 rounded-md transition-colors"
                               >
                                  Cambiar
                               </button>
                           </div>
                         ) : (
                           <form onSubmit={handlePasswordUpdate} className="space-y-4">
                               {/* Campo Contraseña Actual (Descomentar si es necesario) */}
                               {/* <div>...</div> */}
                               <div>
                                   <label htmlFor="new-password"className="block text-sm font-medium text-gray-300 mb-1">Nueva Contraseña</label>
                                   <div className="relative">
                                       <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"/>
                                       <input id="new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500" placeholder="Mínimo 6 caracteres"/>
                                   </div>
                               </div>
                               <div>
                                   <label htmlFor="repeat-password"className="block text-sm font-medium text-gray-300 mb-1">Confirmar Nueva Contraseña</label>
                                   <div className="relative">
                                       <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"/>
                                       <input id="repeat-password" type="password" value={repeatPassword} onChange={e => setRepeatPassword(e.target.value)} required className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500" placeholder="Repite la contraseña"/>
                                   </div>
                               </div>
                               {passwordUpdateStatus.message && (
                                  <p className={`text-sm text-center font-medium ${passwordUpdateStatus.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                                      {passwordUpdateStatus.type === 'error' && <XCircle size={14} className="inline mr-1 align-text-bottom"/>}
                                      {passwordUpdateStatus.type === 'success' && <Check size={14} className="inline mr-1 align-text-bottom"/>}
                                      {passwordUpdateStatus.message}
                                  </p>
                               )}
                               <div className="flex justify-end space-x-3 pt-2">
                                   <button type="button" onClick={() => setIsEditingPassword(false)} className="px-4 py-1.5 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-500 rounded-md transition-colors" disabled={isUpdatingPassword}>Cancelar</button>
                                   <button type="submit" disabled={isUpdatingPassword} className={`px-4 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed`} >
                                       {isUpdatingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
                                       Guardar Cambios
                                   </button>
                               </div>
                           </form>
                         )}
                     </div>
                </section>

                 {/* --- Sección: Plan y Facturación --- */}
                 <section aria-labelledby="plan-heading">
                    <h2 id="plan-heading" className="text-xl font-semibold text-purple-300 mb-4 border-b border-gray-700 pb-2">Plan y Facturación</h2>
                    <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl shadow-lg p-6">
                       <div className="flex justify-between items-center">
                           <div>
                               <h3 className="text-base font-medium text-white">Plan Actual</h3>
                               <p className={`text-sm font-semibold ${userData.isPremium ? 'text-yellow-400' : 'text-gray-300'}`}>{userData.isPremium ? 'Premium ✨' : 'Gratuito'}</p>
                           </div>
                           <Link
                               to="/pricing"
                               className="group relative inline-flex items-center justify-center px-4 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500"
                           >
                               <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)] group-hover:animate-[spin_1s_linear_infinite]"></span>
                               <span className="inline-flex items-center space-x-1 relative z-10">
                                   <CreditCard size={12} />
                                   <span>{userData.isPremium ? 'Gestionar Plan' : 'Ver Planes'}</span>
                               </span>
                           </Link>
                       </div>
                       {/* TODO: Añadir botón para Stripe Customer Portal si el usuario es premium */}
                       {/* {userData.isPremium && <button>Gestionar Suscripción</button>} */}
                    </div>
                 </section>

                {/* --- Sección: Preferencias --- */}
                <section aria-labelledby="preferences-heading">
                   <h2 id="preferences-heading" className="text-xl font-semibold text-purple-300 mb-4 border-b border-gray-700 pb-2">Preferencias</h2>
                   <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl shadow-lg p-6 space-y-5">
                      {/* Idioma */}
                      <div>
                         <label htmlFor="language-select" className="block text-sm font-medium text-gray-300 mb-1.5">Idioma</label>
                         <div className="relative">
                             <Globe size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"/>
                             <select id="language-select" value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full pl-10 pr-8 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 appearance-none">
                               <option value="es">Español</option>
                               <option value="en">English</option>
                             </select>
                             {/* Flecha personalizada */}
                             <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                               <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                             </div>
                         </div>
                      </div>
                      {/* Tema */}
                      <div>
                         <label className="block text-sm font-medium text-gray-300 mb-1.5">Tema</label>
                         <div className="flex space-x-3">
                             <button onClick={() => setTheme('light')} className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${theme === 'light' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}><Sun size={14} /> <span>Claro</span></button>
                             <button onClick={() => setTheme('dark')} className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${theme === 'dark' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}><Moon size={14} /> <span>Oscuro</span></button>
                         </div>
                      </div>
                      {/* Notificaciones */}
                       <div className="flex justify-between items-center pt-2">
                           <div>
                              <label htmlFor="notifications-switch" className="text-sm font-medium text-gray-300 block cursor-pointer">Notificaciones</label>
                              <p className="text-xs text-gray-400">Recibir actualizaciones importantes.</p>
                           </div>
                           <button type="button" id="notifications-switch" onClick={() => setNotificationsEnabled(!notificationsEnabled)} className={`${notificationsEnabled ? 'bg-purple-600' : 'bg-gray-600'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800`} role="switch" aria-checked={notificationsEnabled}>
                               <span aria-hidden="true" className={`${notificationsEnabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}/>
                           </button>
                       </div>
                   </div>
                </section>

                 {/* --- Sección: Eliminar Cuenta --- */}
                 <section aria-labelledby="danger-zone-heading">
                    <h2 id="danger-zone-heading" className="text-xl font-semibold text-red-400 mb-4 border-b border-red-500/50 pb-2">Zona Peligrosa</h2>
                    <div className="bg-red-900/30 border border-red-500/50 rounded-xl shadow-lg p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                            <div>
                               <h3 className="text-base font-medium text-white">Eliminar esta cuenta</h3>
                               <p className="text-sm text-red-200/80 mt-1 max-w-md">Todo tu historial, diseños y preferencias se perderán permanentemente. Esta acción no se puede deshacer.</p>
                            </div>
                            <button onClick={openDeleteModal} className="mt-3 sm:mt-0 sm:ml-4 flex-shrink-0 px-4 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-red-900/30 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed" disabled={isDeletingAccount} >
                               {isDeletingAccount ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 size={14} className="mr-1.5"/>}
                               {isDeletingAccount ? 'Eliminando...' : 'Eliminar Cuenta'}
                            </button>
                        </div>
                        {/* Mostrar error de eliminación aquí si ocurre */}
                        {deleteError && (
                           <p className="mt-3 text-sm text-red-400 flex items-center"><XCircle size={14} className="mr-1.5"/>{deleteError}</p>
                        )}
                    </div>
                 </section>

            </main> {/* <--- CIERRE DE MAIN --- */}

            {/* --- Modal de Confirmación para Eliminar Cuenta --- */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteAccountConfirm}
                title="Confirmar Eliminación de Cuenta"
                message="¿Estás absolutamente seguro? Todos tus datos se perderán permanentemente y esta acción no se puede deshacer."
                isLoading={isDeletingAccount}
            />

        </div> // Cierre del div principal
    );
}

export default Settings;